import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy,
  limit,
  Timestamp 
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { calendarSchedulingService } from './calendarSchedulingService';

/**
 * Persistent Widget Service
 * Manages persistent matrix widgets (Strategic Decision, Strategic Action, Tiger Team)
 * and their completion tracking with scheduling
 */
export const persistentWidgetService = {
  // ========== Widget Management ==========

  /**
   * Get all persistent widgets
   */
  getAllPersistentWidgets: async () => {
    try {
      const widgetsRef = collection(db, 'persistent_widgets');
      const widgetsQuery = query(widgetsRef, orderBy('createdAt', 'desc'));
      const widgetsSnap = await getDocs(widgetsQuery);
      const widgets = [];

      widgetsSnap.forEach((doc) => {
        widgets.push({
          id: doc.id,
          ...doc.data(),
        });
      });

      return { widgets, error: null };
    } catch (error) {
      console.error('Error fetching persistent widgets:', error);
      return { widgets: [], error: error.message };
    }
  },

  /**
   * Get a single persistent widget by ID
   */
  getPersistentWidget: async (widgetId) => {
    try {
      const widgetRef = doc(db, 'persistent_widgets', widgetId);
      const widgetSnap = await getDoc(widgetRef);
      
      if (widgetSnap.exists()) {
        return { widget: { id: widgetSnap.id, ...widgetSnap.data() }, error: null };
      } else {
        return { widget: null, error: 'Widget not found' };
      }
    } catch (error) {
      console.error('Error fetching persistent widget:', error);
      return { widget: null, error: error.message };
    }
  },

  /**
   * Create a new persistent widget
   */
  createPersistentWidget: async (widgetData) => {
    try {
      const widgetsRef = collection(db, 'persistent_widgets');
      const newWidget = {
        ...widgetData,
        createdAt: new Date(),
        updatedAt: new Date(),
      };
      const docRef = await addDoc(widgetsRef, newWidget);
      return { success: true, id: docRef.id, error: null };
    } catch (error) {
      console.error('Error creating persistent widget:', error);
      return { success: false, id: null, error: error.message };
    }
  },

  /**
   * Update a persistent widget
   */
  updatePersistentWidget: async (widgetId, updates) => {
    try {
      const widgetRef = doc(db, 'persistent_widgets', widgetId);
      await updateDoc(widgetRef, {
        ...updates,
        updatedAt: new Date(),
      });
      return { success: true, error: null };
    } catch (error) {
      console.error('Error updating persistent widget:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Delete a persistent widget
   */
  deletePersistentWidget: async (widgetId) => {
    try {
      const widgetRef = doc(db, 'persistent_widgets', widgetId);
      await deleteDoc(widgetRef);
      return { success: true, error: null };
    } catch (error) {
      console.error('Error deleting persistent widget:', error);
      return { success: false, error: error.message };
    }
  },

  // ========== Completion Tracking ==========

  /**
   * Get all completions for a user
   */
  getUserCompletions: async (userId) => {
    try {
      const completionsRef = collection(db, 'persistent_widgets_completions');
      const completionsQuery = query(
        completionsRef,
        where('userId', '==', userId),
        orderBy('completedAt', 'desc')
      );
      const completionsSnap = await getDocs(completionsQuery);
      const completions = [];

      completionsSnap.forEach((doc) => {
        completions.push({
          id: doc.id,
          ...doc.data(),
        });
      });

      return { completions, error: null };
    } catch (error) {
      console.error('Error fetching user completions:', error);
      return { completions: [], error: error.message };
    }
  },

  /**
   * Get completions for a specific widget and user
   */
  getWidgetCompletions: async (widgetId, userId) => {
    try {
      const completionsRef = collection(db, 'persistent_widgets_completions');
      const completionsQuery = query(
        completionsRef,
        where('widgetId', '==', widgetId),
        where('userId', '==', userId),
        orderBy('completedAt', 'desc')
      );
      const completionsSnap = await getDocs(completionsQuery);
      const completions = [];

      completionsSnap.forEach((doc) => {
        completions.push({
          id: doc.id,
          ...doc.data(),
        });
      });

      return { completions, error: null };
    } catch (error) {
      console.error('Error fetching widget completions:', error);
      return { completions: [], error: error.message };
    }
  },

  /**
   * Get the latest completion for a widget and user within a period
   */
  getLatestCompletionInPeriod: async (widgetId, userId, periodKey) => {
    try {
      const completionsRef = collection(db, 'persistent_widgets_completions');
      const completionsQuery = query(
        completionsRef,
        where('widgetId', '==', widgetId),
        where('userId', '==', userId),
        where('periodKey', '==', periodKey),
        orderBy('completedAt', 'desc'),
        limit(1)
      );
      const completionsSnap = await getDocs(completionsQuery);
      
      if (completionsSnap.empty) {
        return { completion: null, error: null };
      }

      const completion = {
        id: completionsSnap.docs[0].id,
        ...completionsSnap.docs[0].data(),
      };

      return { completion, error: null };
    } catch (error) {
      console.error('Error fetching latest completion:', error);
      return { completion: null, error: error.message };
    }
  },

  /**
   * Save a completion for a persistent widget
   */
  saveCompletion: async (completionData) => {
    try {
      const completionsRef = collection(db, 'persistent_widgets_completions');
      const newCompletion = {
        ...completionData,
        completedAt: new Date(),
        createdAt: new Date(),
      };
      const docRef = await addDoc(completionsRef, newCompletion);
      return { success: true, id: docRef.id, error: null };
    } catch (error) {
      console.error('Error saving completion:', error);
      return { success: false, id: null, error: error.message };
    }
  },

  /**
   * Update a completion
   */
  updateCompletion: async (completionId, updates) => {
    try {
      const completionRef = doc(db, 'persistent_widgets_completions', completionId);
      await updateDoc(completionRef, {
        ...updates,
        updatedAt: new Date(),
      });
      return { success: true, error: null };
    } catch (error) {
      console.error('Error updating completion:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Delete a completion
   */
  deleteCompletion: async (completionId) => {
    try {
      const completionRef = doc(db, 'persistent_widgets_completions', completionId);
      await deleteDoc(completionRef);
      return { success: true, error: null };
    } catch (error) {
      console.error('Error deleting completion:', error);
      return { success: false, error: error.message };
    }
  },

  // ========== Scheduling Helpers ==========

  /**
   * Get period key based on schedule type and date
   * Returns a string like "2024-01-15" for daily, "2024-W01" for weekly, "2024-M01" for monthly, etc.
   */
  getPeriodKey: (scheduleType, date = new Date(), schedule = null) => {
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const week = getWeekNumber(date);

    switch (scheduleType) {
      case 'daily':
        return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      case 'weekly':
        // Include day of week in the key for weekly schedules
        if (schedule?.dayOfWeek !== undefined) {
          return `${year}-W${String(week).padStart(2, '0')}-D${schedule.dayOfWeek}`;
        }
        return `${year}-W${String(week).padStart(2, '0')}`;
      case 'monthly':
        // Include day of month in the key for monthly schedules
        if (schedule?.dayOfMonth !== undefined) {
          return `${year}-M${String(month).padStart(2, '0')}-D${schedule.dayOfMonth}`;
        }
        return `${year}-M${String(month).padStart(2, '0')}`;
      case 'bi-weekly':
        const biWeek = Math.floor(week / 2);
        return `${year}-BW${String(biWeek).padStart(2, '0')}`;
      case 'quarterly':
        const quarter = Math.floor((month - 1) / 3) + 1;
        return `${year}-Q${quarter}`;
      default:
        return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    }
  },

  /**
   * Check if a widget is available for completion based on schedule
   */
  isWidgetAvailable: async (widget, userId) => {
    if (!widget.schedule || widget.status !== 'Active') {
      // No schedule or inactive means not available
      return { isAvailable: false, nextAvailableDate: null, hasCompleted: false };
    }

    // Check if today is the right day for the schedule
    const shouldShowToday = persistentWidgetService.shouldShowToday(widget.schedule);
    console.log('shouldShowToday', shouldShowToday);
    if (!shouldShowToday) {
      return { isAvailable: false, nextAvailableDate: getNextScheduledDate(widget.schedule), hasCompleted: false };
    }

    const periodKey = persistentWidgetService.getPeriodKey(widget.schedule.type, new Date(), widget.schedule);
    const { completion } = await persistentWidgetService.getLatestCompletionInPeriod(
      widget.id,
      userId,
      periodKey
    );

    return {
      isAvailable: !completion, // Available if not completed this period
      nextAvailableDate: completion ? getNextScheduledDate(widget.schedule) : null,
      hasCompleted: !!completion,
      currentPeriodKey: periodKey,
    };
  },

  /**
   * Check if widget should be shown today based on schedule
   * For weekly: checks if we're in the same week period (from scheduled day to next scheduled day)
   * For monthly: checks if we're in the same month period (from scheduled day to next scheduled day)
   * For daily: always true
   */
  shouldShowToday: (schedule) => {
    if (!schedule || !schedule.type) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dayOfWeek = today.getDay(); // 0 = Sunday, 6 = Saturday

    switch (schedule.type) {
      case 'daily':
        return true;
      case 'weekly':
        // Check if we're in the same week period as the scheduled day
        if (schedule.dayOfWeek === undefined) return false;
        
        // Get the start of the current week (Sunday = 0)
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - dayOfWeek);
        weekStart.setHours(0, 0, 0, 0);
        
        // Get the scheduled day of this week
        const scheduledDayThisWeek = new Date(weekStart);
        scheduledDayThisWeek.setDate(weekStart.getDate() + schedule.dayOfWeek);
        scheduledDayThisWeek.setHours(0, 0, 0, 0);
        
        // Get the scheduled day of next week (to determine period end)
        const scheduledDayNextWeek = new Date(scheduledDayThisWeek);
        scheduledDayNextWeek.setDate(scheduledDayThisWeek.getDate() + 7);
        
        // Check if today is on or after the scheduled day this week, but before next week's scheduled day
        return today >= scheduledDayThisWeek && today < scheduledDayNextWeek;
      case 'monthly':
        // Check if we're in the same month period (from scheduled day to next month's scheduled day)
        if (schedule.dayOfMonth === undefined) return false;
        
        const scheduledDayThisMonth = new Date(today.getFullYear(), today.getMonth(), schedule.dayOfMonth);
        scheduledDayThisMonth.setHours(0, 0, 0, 0);
        
        // Get the scheduled day of next month
        const scheduledDayNextMonth = new Date(today.getFullYear(), today.getMonth() + 1, schedule.dayOfMonth);
        scheduledDayNextMonth.setHours(0, 0, 0, 0);
        
        // Check if today is on or after the scheduled day this month, but before next month's scheduled day
        return today >= scheduledDayThisMonth && today < scheduledDayNextMonth;
      default:
        return false;
    }
  },

  /**
   * Check if user has completed widget for current period (similar to hasCompletedToday for daily decisions)
   */
  hasCompletedForPeriod: async (userId, widget) => {
    console.log('hasCompletedForPeriod', userId, widget);
    if (!widget || !widget.schedule || !widget.id) return false;

    try {
      const periodKey = persistentWidgetService.getPeriodKey(widget.schedule.type, new Date(), widget.schedule);
      const { completion } = await persistentWidgetService.getLatestCompletionInPeriod(
        widget.id,
        userId,
        periodKey
      );
      console.log('completion', completion);
      return !!completion;
    } catch (error) {
      console.error('Error checking completion for period:', error);
      return false;
    }
  },

  /**
   * Get active widgets that should be shown to user (excluding completed ones)
   */
  getActiveWidgetsForUser: async (userId) => {
    try {
      // Get all persistent widgets
      const { widgets, error } = await persistentWidgetService.getAllPersistentWidgets();
      if (error) {
        return { widgets: [], error };
      }

      // Filter to only active widgets
      const activeWidgets = widgets.filter(w => w.status === 'Active' && w.schedule);

      // Check which ones should be shown today
      const widgetsToShow = [];
      for (const widget of activeWidgets) {
        const shouldShow = persistentWidgetService.shouldShowToday(widget.schedule);
        if (shouldShow) {
          const hasCompleted = await persistentWidgetService.hasCompletedForPeriod(userId, widget);
          if (!hasCompleted) {
            widgetsToShow.push(widget);
          }
        }
      }

      return { widgets: widgetsToShow, error: null };
    } catch (error) {
      console.error('Error getting active widgets for user:', error);
      return { widgets: [], error: error.message };
    }
  },

  /**
   * Get all widgets that should be shown to user (including completed ones)
   * Used for displaying widgets in the indicator
   */
  getAllWidgetsForUser: async (userId) => {
    try {
      // Get all persistent widgets
      const { widgets, error } = await persistentWidgetService.getAllPersistentWidgets();
      if (error) {
        return { widgets: [], error };
      }

      // Filter to only active widgets
      const activeWidgets = widgets.filter(w => w.status === 'Active' && w.schedule);

      // Check which ones should be shown today (including completed ones)
      const widgetsToShow = [];
      for (const widget of activeWidgets) {
        const shouldShow = persistentWidgetService.shouldShowToday(widget.schedule);
        if (shouldShow) {
          widgetsToShow.push(widget);
        }
      }

      return { widgets: widgetsToShow, error: null };
    } catch (error) {
      console.error('Error getting all widgets for user:', error);
      return { widgets: [], error: error.message };
    }
  },

  /**
   * Mark widget as completed for current period
   */
  markCompleted: async (userId, widgetId, widgetData = {}) => {
    try {
      // Get the widget to access its schedule
      const { widget, error: widgetError } = await persistentWidgetService.getPersistentWidget(widgetId);
      if (widgetError || !widget) {
        return { success: false, error: widgetError || 'Widget not found' };
      }

      if (!widget.schedule) {
        return { success: false, error: 'Widget has no schedule' };
      }

      const periodKey = persistentWidgetService.getPeriodKey(widget.schedule.type, new Date(), widget.schedule);
      
      const completionData = {
        userId,
        widgetId,
        widgetType: widget.type,
        periodKey,
        schedule: widget.schedule,
        widgetData, // Store any widget-specific data (matrix entries, etc.)
        completedAt: new Date(),
      };

      const result = await persistentWidgetService.saveCompletion(completionData);
      
      // Map widget type to calendar event type and mark as completed
      const calendarEventTypeMap = {
        'TigerTeamBreakoutsTracker': 'tigerTeamBreakouts',
        'StrategicActionMatrix': 'strategicAction',
        'StrategicDecisionMatrix': 'strategicDecision',
      };
      const calendarEventType = calendarEventTypeMap[widget.type];
      if (calendarEventType) {
        await calendarSchedulingService.markScheduledEventComplete(userId, calendarEventType, new Date());
      }
      
      return result;
    } catch (error) {
      console.error('Error marking widget as completed:', error);
      return { success: false, error: error.message };
    }
  },
};

/**
 * Helper: Get week number of the year
 */
function getWeekNumber(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
}

/**
 * Helper: Get next scheduled date based on schedule
 */
function getNextScheduledDate(schedule) {
  if (!schedule || !schedule.type) return null;

  const now = new Date();
  const next = new Date(now);

  switch (schedule.type) {
    case 'daily':
      next.setDate(now.getDate() + 1);
      next.setHours(0, 0, 0, 0);
      break;
    case 'weekly':
      if (schedule.dayOfWeek !== undefined) {
        const daysUntilNext = (schedule.dayOfWeek - now.getDay() + 7) % 7;
        next.setDate(now.getDate() + (daysUntilNext === 0 ? 7 : daysUntilNext));
        next.setHours(0, 0, 0, 0);
      } else {
        next.setDate(now.getDate() + (7 - now.getDay()));
      }
      break;
    case 'monthly':
      if (schedule.dayOfMonth !== undefined) {
        next.setMonth(now.getMonth() + 1);
        next.setDate(schedule.dayOfMonth);
        next.setHours(0, 0, 0, 0);
      } else {
        next.setMonth(now.getMonth() + 1, 1);
      }
      break;
    case 'bi-weekly':
      const daysUntilNextBiWeek = (14 - (now.getDay() % 14));
      next.setDate(now.getDate() + daysUntilNextBiWeek);
      break;
    case 'quarterly':
      const quarter = Math.floor(now.getMonth() / 3);
      next.setMonth((quarter + 1) * 3, 1);
      break;
    default:
      next.setDate(now.getDate() + 1);
  }

  return next;
}

/**
 * Helper: Get next period date based on schedule type (legacy function, kept for compatibility)
 */
function getNextPeriodDate(scheduleType) {
  return getNextScheduledDate({ type: scheduleType });
}

